import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Timer, ClipboardCheck, Crown, Sparkles, History, Check,
} from "lucide-react";
import { motion } from "framer-motion";
import {
  listCurriculumSubjects, startMockExam, mockExamHistory,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { formatMinutes } from "@/lib/time";

/** One pill style for every toggle group: lavender fill + check icon when selected. */
function TogglePill({ selected, onClick, children, testId }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      data-checked={selected}
      data-testid={testId}
      onClick={onClick}
      className="nb-toggle flex items-center gap-1.5"
    >
      {selected && <Check className="w-3.5 h-3.5" aria-hidden="true" strokeWidth={3} />}
      <span>{children}</span>
    </button>
  );
}

export default function MockExam() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [subject, setSubject] = useState(params.get("subject") || "");
  const [count, setCount] = useState(15);
  const [duration, setDuration] = useState(30);
  const [extraTime, setExtraTime] = useState(false);
  const [starting, setStarting] = useState(false);
  const [history, setHistory] = useState([]);

  const isPremium = user?.subscription_status === "active" || user?.subscription_status === "trialing";
  const activeChildId = user?.active_child_id;
  const finalDuration = extraTime ? Math.round(duration * 1.25) : duration;

  useEffect(() => {
    listCurriculumSubjects().then((s) => {
      setSubjects(s);
      if (s.length && !subject) setSubject(s[0].subject);
    });
    mockExamHistory().then(setHistory).catch(() => {});
  }, []); // eslint-disable-line

  const begin = async () => {
    if (!isPremium) return navigate("/pricing");
    if (!activeChildId) {
      toast.error("Pick a child profile first");
      return navigate("/family");
    }
    if (!subject) return;
    setStarting(true);
    try {
      const exam = await startMockExam(subject, {
        question_count: Number(count),
        duration_minutes: Number(finalDuration),
      });
      navigate(`/exam/${exam.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start");
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-zinc-950" data-testid="mock-exam-launcher">
      <PageHeader backTo={-1} backLabel="Back" title="Mock exam" />

      <main className="max-w-[960px] mx-auto p-6 sm:p-10">
        <div className="mb-8">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">
            Take a{" "}
            <span className="bg-[#FFB38E] dark:bg-[#F6AD55] px-3 nb-border rounded-2xl inline-block text-black">
              real mock
            </span>
          </h1>
          <p className="text-muted font-medium mt-3 max-w-xl text-base">
            Questions from every topic in the subject, against the clock. The timer keeps running if you close the tab and hands your paper in when time&apos;s up.
          </p>
        </div>

        <fieldset
          aria-disabled={!isPremium}
          className={`nb-card p-6 sm:p-8 space-y-6 ${!isPremium ? "opacity-50 pointer-events-none select-none" : ""}`}
        >
          <legend className="sr-only">Mock exam options</legend>

          <div role="radiogroup" aria-labelledby="exam-subject-label">
            <div id="exam-subject-label" className="label-caps block mb-3">Subject</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {subjects.map((s) => (
                <button
                  key={s.subject}
                  type="button"
                  role="radio"
                  aria-checked={subject === s.subject}
                  data-checked={subject === s.subject}
                  data-testid={`exam-subject-${s.subject}`}
                  onClick={() => setSubject(s.subject)}
                  className="nb-toggle text-left flex items-center gap-2"
                >
                  {subject === s.subject && <Check className="w-4 h-4" aria-hidden="true" strokeWidth={3} />}
                  <div className="font-display text-lg font-extrabold">{s.subject}</div>
                  <span className="ml-auto text-xs text-muted">{s.count} topics</span>
                </button>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div role="radiogroup" aria-labelledby="exam-count-label">
              <div id="exam-count-label" className="label-caps block mb-3 flex items-center gap-2">
                <ClipboardCheck className="w-4 h-4" aria-hidden="true" /> Questions
              </div>
              <div className="flex gap-2 flex-wrap">
                {[10, 15, 20, 30].map((n) => (
                  <TogglePill key={n} selected={count === n} onClick={() => setCount(n)} testId={`exam-count-${n}`}>
                    {n}
                  </TogglePill>
                ))}
              </div>
            </div>
            <div role="radiogroup" aria-labelledby="exam-duration-label">
              <div id="exam-duration-label" className="label-caps block mb-3 flex items-center gap-2">
                <Timer className="w-4 h-4" aria-hidden="true" /> Duration
              </div>
              <div className="flex gap-2 flex-wrap">
                {[15, 30, 45, 60].map((n) => (
                  <TogglePill key={n} selected={duration === n} onClick={() => setDuration(n)} testId={`exam-duration-${n}`}>
                    {formatMinutes(n)}
                  </TogglePill>
                ))}
              </div>
              <label className="mt-3 flex items-center gap-2 text-sm font-bold cursor-pointer">
                <input
                  type="checkbox"
                  checked={extraTime}
                  onChange={(e) => setExtraTime(e.target.checked)}
                  data-testid="exam-extra-time"
                  className="w-4 h-4"
                />
                <span>Extra time (+25%) {extraTime && <span className="text-muted font-medium">= {formatMinutes(finalDuration)}</span>}</span>
              </label>
            </div>
          </div>
        </fieldset>

        {!isPremium ? (
          <>
            <div className="nb-container p-5 mt-5 flex items-center gap-3 bg-[#FFE066] text-black" data-testid="exam-premium-banner">
              <Crown className="w-6 h-6 flex-shrink-0" aria-hidden="true" />
              <div className="min-w-0">
                <div className="font-display text-lg font-extrabold">Premium feature</div>
                <div className="text-sm font-medium">Mock exams are part of Premium.</div>
              </div>
            </div>
            <button
              onClick={() => navigate("/pricing")}
              data-testid="exam-unlock-btn"
              className="nb-button-primary w-full py-4 mt-3 flex items-center justify-center gap-2 font-display text-lg"
            >
              <Crown className="w-5 h-5" aria-hidden="true" strokeWidth={3} />
              Unlock mock exams
            </button>
          </>
        ) : (
          <button
            onClick={begin}
            disabled={starting || !subject}
            data-testid="exam-start-btn"
            className="nb-button-primary w-full py-4 mt-5 flex items-center justify-center gap-2 font-display text-lg"
          >
            <Sparkles className="w-5 h-5" aria-hidden="true" strokeWidth={3} />
            {starting ? "Preparing your exam…" : `Start ${count}-question mock`}
          </button>
        )}

        {history.length > 0 && (
          <section className="mt-10">
            <div className="flex items-center gap-2 mb-4">
              <History className="w-5 h-5" aria-hidden="true" />
              <h2 className="font-display text-2xl font-extrabold">Past attempts</h2>
            </div>
            <div className="space-y-3">
              {history.map((h, i) => (
                <motion.button
                  key={h.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                  onClick={() => navigate(`/exam/${h.id}`)}
                  data-testid={`exam-history-${h.id}`}
                  className="nb-card w-full p-4 text-left flex items-center gap-4"
                >
                  <div className={`w-14 h-14 rounded-xl nb-border flex items-center justify-center tabular-nums text-xl font-black text-black ${
                    h.score_pct >= 80 ? "bg-[#9DFFB0]" : h.score_pct >= 60 ? "bg-[#FFE066]" : "bg-[#FFB38E]"
                  }`}>
                    {h.score_pct}%
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg font-extrabold">{h.subject}</div>
                    <div className="text-xs font-bold text-muted">
                      {h.correct}/{h.total} correct · {formatMinutes((h.seconds_taken || 0) / 60)} · {new Date(h.submitted_at).toLocaleDateString()}
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
